import { HttpClient } from '@lemoment/api-client';
import { API_ENDPOINT } from '@lemoment/shared';
import type { AuthRefreshResponse } from '@lemoment/shared';

import { useAuthStore } from './authStore';

/**
 * Exchange the stored refresh token for a fresh access token. Uses a raw fetch
 * (not apiClient) so a 401 here can't recurse back into the refresh flow.
 */
const refreshAccessToken = async (): Promise<boolean> => {
  const { refreshToken, setAccessToken } = useAuthStore.getState();
  if (!refreshToken) {
    return false;
  }
  try {
    const res = await fetch(`${API_ENDPOINT}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) {
      return false;
    }
    const { tokens } = (await res.json()) as AuthRefreshResponse;
    setAccessToken(tokens.accessToken);
    return true;
  } catch {
    return false;
  }
};

/**
 * App-wide HTTP client. Token is read from the auth store on every request;
 * a 401 first tries a token refresh + one retry, and only clears auth (so the
 * route guard redirects to /login) when the refresh itself fails.
 */
export const apiClient = new HttpClient({
  baseUrl: API_ENDPOINT,
  getToken: async () => useAuthStore.getState().accessToken,
  refresh: refreshAccessToken,
  onUnauthorized: () => useAuthStore.getState().clear(),
});
